import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import api from '../services/api.ts';
import FileUpload, { FileAttachment } from '../components/FileUpload.tsx';
import { formatCurrencyInput, parseCurrency, applyCurrencyMask } from '../utils/currency.ts';
import {
  ArrowLeftIcon,
  TruckIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

// Types
interface VehicleFormData {
  acquisitionValue: number;
  currentValue: number;
  acquisitionDate: string;
  attachments: FileAttachment[];
  details: {
    make: string;
    model: string;
    year: number;
    licensePlate: string;
    color: string;
    notes?: string;
  };
}

const CreateVehicle: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<VehicleFormData>({
    acquisitionValue: 0,
    currentValue: 0,
    acquisitionDate: new Date().toISOString().split('T')[0],
    attachments: [],
    details: {
      make: '',
      model: '',
      year: new Date().getFullYear(),
      licensePlate: '',
      color: '',
    },
  });

  // Estados para os valores formatados dos inputs
  const [acquisitionValueDisplay, setAcquisitionValueDisplay] = useState<string>('');
  const [currentValueDisplay, setCurrentValueDisplay] = useState<string>('');

  const [errors, setErrors] = useState<Record<string, string>>({});

  const vehicleCategories = [
    { value: 'crlv', label: 'CRLV' },
    { value: 'manual', label: 'Manual' },
    { value: 'nota_fiscal', label: 'Nota Fiscal' },
    { value: 'fotos', label: 'Fotos' },
    { value: 'outros', label: 'Outros' },
  ];

  // Mutation para criar veículo
  const createVehicleMutation = useMutation(
    async (data: VehicleFormData) => {
      let uploadedFiles: any[] = [];
      
      // Primeiro, fazer upload dos arquivos se existirem
      if (data.attachments && data.attachments.length > 0) {
        const formData = new FormData();
        data.attachments.forEach((attachment: any) => {
          formData.append('files', attachment.file);
        });

        const uploadResponse = await api.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (uploadResponse.data.success) {
          uploadedFiles = uploadResponse.data.data.files.map((file: any, index: number) => ({
            id: Date.now() + index,
            name: file.originalName,
            filename: file.filename,
            url: file.url,
            size: file.size,
            mimetype: file.mimetype,
            type: data.attachments[index].type,
            category: data.attachments[index].category,
          }));
        }
      }

      // Criar o veículo com os anexos já salvos - mapeando para os campos esperados pelo backend
      const vehicleData = {
        make: data.details.make,
        model: data.details.model,
        year: data.details.year,
        licensePlate: data.details.licensePlate,
        color: data.details.color,
        purchasePrice: data.acquisitionValue,
        sellingPrice: data.currentValue || null,
        purchaseDate: data.acquisitionDate,
        attachments: uploadedFiles, // Substituir pelos arquivos já salvos
      };

      const response = await api.post('/vehicles', vehicleData);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('vehicles');
        navigate('/vehicles');
      },
      onError: (error: any) => {
        console.error('Erro ao criar veículo:', error);
      },
    }
  );

  const handleCurrencyChange = (field: 'acquisitionValue' | 'currentValue', value: string) => {
    const maskedValue = applyCurrencyMask(value);
    const numericValue = parseCurrency(maskedValue);

    if (field === 'acquisitionValue') {
      setAcquisitionValueDisplay(maskedValue);
    } else {
      setCurrentValueDisplay(maskedValue);
    }

    setFormData(prev => ({
      ...prev,
      [field]: numericValue,
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Limpar erro se existir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleDetailsChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      details: {
        ...prev.details,
        [field]: value,
      },
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.acquisitionValue <= 0) {
      newErrors.acquisitionValue = 'Valor de aquisição deve ser maior que zero';
    }

    if (!formData.acquisitionDate) {
      newErrors.acquisitionDate = 'Data de aquisição é obrigatória';
    }

    // Validações específicas para veículos
    if (!formData.details.make?.trim()) {
      newErrors.make = 'Marca é obrigatória';
    }

    if (!formData.details.model?.trim()) {
      newErrors.model = 'Modelo é obrigatório';
    }

    if (!formData.details.year || formData.details.year < 1900) {
      newErrors.year = 'Ano é obrigatório e deve ser válido';
    }

    if (!formData.details.licensePlate?.trim()) {
      newErrors.licensePlate = 'Placa é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      createVehicleMutation.mutate(formData);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/vehicles')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Voltar para Veículos
          </button>

          <h1 className="text-3xl font-bold text-gray-900">Criar Novo Veículo</h1>
          <p className="text-gray-600 mt-2">Adicione um novo veículo ao seu patrimônio</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Informações Básicas */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <DocumentTextIcon className="h-6 w-6 mr-2 text-blue-600" />
              Informações Básicas
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label
                  htmlFor="acquisitionDate"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Data de Aquisição *
                </label>
                <input
                  id="acquisitionDate"
                  type="date"
                  value={formData.acquisitionDate}
                  onChange={e => handleInputChange('acquisitionDate', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.acquisitionDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.acquisitionDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.acquisitionDate}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="acquisitionValue"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Valor de Aquisição *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="acquisitionValue"
                    type="text"
                    value={acquisitionValueDisplay}
                    onChange={e => handleCurrencyChange('acquisitionValue', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.acquisitionValue ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0,00"
                  />
                </div>
                {errors.acquisitionValue && (
                  <p className="mt-1 text-sm text-red-600">{errors.acquisitionValue}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="currentValue"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Valor de Venda
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="currentValue"
                    type="text"
                    value={currentValueDisplay}
                    onChange={e => handleCurrencyChange('currentValue', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.currentValue ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0,00"
                  />
                </div>
                {errors.currentValue && (
                  <p className="mt-1 text-sm text-red-600">{errors.currentValue}</p>
                )}
              </div>
            </div>
          </div>

          {/* Detalhes do Veículo */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h4 className="text-lg font-medium text-gray-900 flex items-center mb-6">
              <TruckIcon className="h-5 w-5 mr-2 text-green-600" />
              Detalhes do Veículo
            </h4>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="make" className="block text-sm font-medium text-gray-700 mb-2">
                  Marca *
                </label>
                <input
                  id="make"
                  type="text"
                  value={formData.details.make}
                  onChange={e => handleDetailsChange('make', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.make ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Honda, Toyota, Ford"
                />
                {errors.make && <p className="mt-1 text-sm text-red-600">{errors.make}</p>}
              </div>

              <div>
                <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-2">
                  Modelo *
                </label>
                <input
                  id="model"
                  type="text"
                  value={formData.details.model}
                  onChange={e => handleDetailsChange('model', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.model ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Civic, Corolla, Focus"
                />
                {errors.model && <p className="mt-1 text-sm text-red-600">{errors.model}</p>}
              </div>

              <div>
                <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-2">
                  Ano *
                </label>
                <input
                  id="year"
                  type="number"
                  value={formData.details.year}
                  onChange={e => handleDetailsChange('year', parseInt(e.target.value) || new Date().getFullYear())}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.year ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="2020"
                  min="1900"
                  max={new Date().getFullYear() + 1}
                />
                {errors.year && <p className="mt-1 text-sm text-red-600">{errors.year}</p>}
              </div>

              <div>
                <label htmlFor="licensePlate" className="block text-sm font-medium text-gray-700 mb-2">
                  Placa *
                </label>
                <input
                  id="licensePlate"
                  type="text"
                  value={formData.details.licensePlate}
                  onChange={e => handleDetailsChange('licensePlate', e.target.value.toUpperCase())}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.licensePlate ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="ABC-1234"
                />
                {errors.licensePlate && <p className="mt-1 text-sm text-red-600">{errors.licensePlate}</p>}
              </div>

              <div>
                <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-2">
                  Cor
                </label>
                <input
                  id="color"
                  type="text"
                  value={formData.details.color}
                  onChange={e => handleDetailsChange('color', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Branco, Preto, Prata"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                  Observações
                </label>
                <textarea
                  id="notes"
                  rows={3}
                  value={formData.details.notes || ''}
                  onChange={e => handleDetailsChange('notes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Informações adicionais sobre o veículo..."
                />
              </div>
            </div>
          </div>

          {/* Anexos - Fotos e Documentos */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-blue-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Fotos e Documentos</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Adicione fotos do veículo e documentos como documento do carro (CRLV), nota fiscal, manual, etc.
            </p>

            <FileUpload
              files={formData.attachments}
              onFilesChange={files => setFormData(prev => ({ ...prev, attachments: files }))}
              maxFiles={20}
              maxSizePerFile={10}
              showCategories={true}
              categories={vehicleCategories}
            />
          </div>

          {/* Botões */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/vehicles')}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createVehicleMutation.isLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createVehicleMutation.isLoading ? 'Criando...' : 'Criar Veículo'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateVehicle;
